import { ORDER_STATUS } from "@/helpers/order/status";

const TERMINAL_STATUSES = [
  ORDER_STATUS.COMPLETED,
  ORDER_STATUS.CANCELLED,
  ORDER_STATUS.REFUNDED
];

export const isTerminalOrderStatus = (status) => TERMINAL_STATUSES.includes(status);

export const cloneOrderForSync = (order) => {
  if (!order) return null;
  try {
    return JSON.parse(JSON.stringify(order));
  } catch (e) {
    return { ...order };
  }
};

export const getOrderSyncKey = (order) => {
  if (!order) return null;
  return order.local_id || (order.id ? String(order.id) : null);
};

export const isQueueableOrder = (order) => {
  if (!order) return false;
  const key = getOrderSyncKey(order);
  if (!key) return false;

  // id:0/localID:0 objects are UI placeholders until they receive content.
  const hasItems = Array.isArray(order.items) && order.items.length > 0;
  const hasPayments = Array.isArray(order.payments) && order.payments.length > 0;
  if (!hasItems && !hasPayments && !isTerminalOrderStatus(order.status)) return false;

  return true;
};

export const normalizeOrderForSync = (order) => {
  const payload = cloneOrderForSync(order);
  if (!payload) return null;

  const nowIso = new Date().toISOString();
  payload.open_date = payload.open_date || payload.created_at || nowIso;
  payload.updated_at = payload.updated_at || nowIso;

  if (payload.id) {
    payload.number = payload.id.toString();
  } else if (!payload.number) {
    payload.number = "0";
  }

  if (payload.id && Array.isArray(payload.payments)) {
    payload.payments = payload.payments.map(payment => ({
      ...payment,
      order: payload.id
    }));
  }

  if (payload.table) {
    payload.table_id = payload.table;
  }

  return payload;
};

export const getOrderSyncRevision = (order) => {
  if (!order) return "";
  return getOrderSyncStateSignature(order);
};

export const getOrderSyncStateSignature = (order) => {
  if (!order) return "";
  const items = Array.isArray(order.items) ? order.items : [];
  const payments = Array.isArray(order.payments) ? order.payments : [];
  const itemRevision = items
    .map(item => [
      item.id || item.local_id || "",
      item.product || "",
      item.quantity || "",
      item.price || "",
      item.subtotal || "",
      item.grand_total || "",
      item.discount || 0,
      item.notes || "",
      item.void_status || 0,
      item.void_reason || "",
      item.voided_at || ""
    ].join(":"))
    .join("|");
  const paymentRevision = payments
    .map(payment => [
      payment.id || payment.local_id || "",
      payment.amount_paid || payment.amount || "",
      payment.payment_type || "",
      payment.payment_date || ""
    ].join(":"))
    .join("|");
  const customer = order.customer && typeof order.customer === "object"
    ? (order.customer.id || order.customer.local_id || order.customer.phone || "")
    : (order.customer || "");

  return [
    order.status || "",
    order.close_date || "",
    order.hold_date || "",
    order.table || "",
    order.dining_option_type || "",
    order.dining_option || "",
    customer,
    order.notes || "",
    order.description || "",
    order.subtotal || 0,
    order.tax_total || 0,
    order.discount_total || 0,
    order.order_discount || 0,
    order.discount_orders || 0,
    order.grand_total || 0,
    order.balance_remaining || 0,
    items.length,
    payments.length,
    itemRevision,
    paymentRevision
  ].join("::");
};

export const buildOrderSyncOutboxEntry = (order, trigger = "unknown") => {
  if (!isQueueableOrder(order)) return null;
  const payload = normalizeOrderForSync(order);
  const key = getOrderSyncKey(payload);
  if (!key) return null;

  const nowIso = new Date().toISOString();
  return {
    key,
    order_id: payload.id || null,
    order_local_id: payload.local_id || null,
    payload,
    revision: getOrderSyncRevision(payload),
    trigger,
    attempts: 0,
    last_error: null,
    created_at: nowIso,
    updated_at: nowIso,
    next_retry_at: null
  };
};
